import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-semibold uppercase tracking-wide transition-all duration-200 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-steel text-white hover:bg-red-600",
        amber: "bg-red-600 text-white hover:bg-red-700",
        outline:
          "border-2 border-steel bg-transparent text-steel hover:bg-red-600 hover:text-white hover:border-red-600",
        ghost: "bg-transparent text-steel hover:bg-red-50 hover:text-red-700",
        destructive: "bg-red-700 text-white hover:bg-red-800",
        link: "text-steel underline-offset-4 hover:text-red-600 normal-case font-medium tracking-normal",
        cart: "bg-steel text-white hover:bg-red-600 hover:shadow-md",
        checkout: "bg-red-600 text-white hover:bg-red-700 hover:shadow-lg w-full",
      },
      size: {
        default: "h-11 px-6 py-2",
        sm: "h-9 px-4 text-xs",
        lg: "h-14 px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };